import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  getLangFromPath,
  stripLangFromPath,
  localizePath,
  DEFAULT_LANGUAGE,
} from '../i18n';

export default function LanguageSwitcher({ compact = false }) {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef(null);

  const currentCode = getLangFromPath(location.pathname);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === currentCode) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (i18n.language !== currentCode) i18n.changeLanguage(currentCode);
    if (typeof document !== 'undefined') document.documentElement.lang = currentCode;
  }, [currentCode, i18n]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const switchTo = (code) => {
    const cleanPath = stripLangFromPath(location.pathname);
    const newPath = code === DEFAULT_LANGUAGE ? cleanPath : localizePath(cleanPath, code);
    try { localStorage.setItem('i18nextLng', code); } catch {}
    navigate(newPath + location.search + location.hash);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        data-testid="language-switcher-button"
        className={`inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors ${
          compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
        } text-white/80 hover:text-white font-semibold`}
      >
        <span aria-hidden="true">{current.flag}</span>
        <span className="uppercase tracking-wider">{current.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-44 rounded-xl bg-[#111116] border border-white/10 shadow-2xl shadow-black/60 overflow-hidden z-50"
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const active = l.code === currentCode;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => switchTo(l.code)}
                data-testid={`language-option-${l.code}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  active ? 'bg-violet-500/15 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span aria-hidden="true" className="text-base leading-none">{l.flag}</span>
                <span className="flex-1 text-left">{l.name}</span>
                {active && <Check className="w-3.5 h-3.5 text-violet-300" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
